<template>
  <div class="mt-1">
    <b-form>
      <b-row>
        <!-- Boolean Fields Section -->
        <b-col cols="12" md="6">
          <b-form-checkbox
            id="swingerBypass"
            :checked="zoneForm.swingerBypass === 'Y'"
            @change="zoneForm.swingerBypass = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.swingerBypass"
            class="mb-1"
          >
            Swinger Bypass
          </b-form-checkbox>

          <b-form-checkbox
            id="zoneRetard"
            :checked="zoneForm.zoneRetard === 'Y'"
            @change="zoneForm.zoneRetard = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.zoneRetard"
            class="mb-1"
          >
            Retard
          </b-form-checkbox>

          <b-form-checkbox
            id="fastResponse"
            :checked="zoneForm.fastResponse === 'Y'"
            @change="zoneForm.fastResponse = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.fastResponse"
            class="mb-1"
          >
            Fast Response
          </b-form-checkbox>

          <b-form-checkbox
            id="crossZone"
            :checked="zoneForm.crossZone === 'Y'"
            @change="zoneForm.crossZone = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.crossZone"
            class="mb-1"
          >
            Cross Zone
          </b-form-checkbox>

          <b-form-checkbox
            id="priority"
            :checked="zoneForm.priority === 'Y'"
            @change="zoneForm.priority = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.priority"
            class="mb-1"
          >
            Priority Zone
          </b-form-checkbox>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-checkbox
            id="firePanelSlaveInput"
            :checked="zoneForm.firePanelSlaveInput === 'Y'"
            @change="zoneForm.firePanelSlaveInput = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.firePanelSlaveInput"
            class="mb-1"
          >
            Fire Panel Slave
          </b-form-checkbox>

          <b-form-checkbox
            id="zoneRealTimeStatus"
            :checked="zoneForm.zoneRealTimeStatus === 'Y'"
            @change="zoneForm.zoneRealTimeStatus = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.zoneRealTimeStatus"
            class="mb-1"
          >
            Real-time Status
          </b-form-checkbox>

          <b-form-checkbox
            id="trafficCount"
            :checked="zoneForm.trafficCount === 'Y'"
            @change="zoneForm.trafficCount = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.trafficCount"
            class="mb-1"
          >
            Traffic Count
          </b-form-checkbox>

          <b-form-checkbox
            id="lockdown"
            :checked="zoneForm.lockdown === 'Y'"
            @change="zoneForm.lockdown = $event ? 'Y' : 'N'"
            :disabled="isSaving || !advancedFields.lockdown"
            class="mb-1"
          >
            Lockdown
          </b-form-checkbox>
        </b-col>

        <!-- Additional Fields -->
        <b-col cols="12" md="6">
          <b-form-group label="Zone Audit Days" label-for="zoneAuditDays">
            <validation-provider
              #default="{ errors }"
              name="Zone Audit Days"
              rules="numeric|min_value:0|max_value:999"
            >
              <b-form-input
                id="zoneAuditDays"
                v-model="zoneForm.zoneAuditDays"
                type="text"
                :state="errors.length > 0 ? false : null"
                maxlength="3"
                :disabled="!advancedFields.zoneAuditDays"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group
            label="Report with Account # for Area"
            label-for="reportWithAccountNumber"
          >
            <b-form-select
              id="reportWithAccountNumber"
              v-model="zoneForm.reportWithAccountNumber"
              :options="areaSelectOptions"
              :disabled="!advancedFields.reportWithAccountNumber"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Chime Sound" label-for="chimeSong">
            <b-form-select
              id="chimeSong"
              v-model="zoneForm.chimeSong"
              :options="chimeSongOptions"
              :disabled="!advancedFields.chimeSong"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Prewarn Keypads" label-for="prewarPresignal">
            <b-form-input
              id="prewarPresignal"
              v-model="zoneForm.prewarPresignal"
              type="text"
              placeholder="1-16"
              :disabled="!advancedFields.prewarPresignal"
            />
            <b-form-text>Format: 1-16</b-form-text>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Entry Delay Number" label-for="entryDelayNumber">
            <validation-provider
              #default="{ errors }"
              name="Entry Delay Number"
              rules="numeric|min_value:1|max_value:4"
            >
              <b-form-input
                id="entryDelayNumber"
                v-model="zoneForm.entryDelayNumber"
                type="text"
                :state="errors.length > 0 ? false : null"
                maxlength="1"
                placeholder="1"
                :disabled="!advancedFields.entryDelayNumber"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { computed, toRefs } from "@vue/composition-api";
import { ValidationProvider } from "vee-validate";
import { CHIME_SONG_OPTIONS } from "./useZoneConfig";
import { useZoneFieldsEnabled } from "./useZoneFieldsEnabled";
import store from "@/store";

export default {
  name: "ZoneAdvanced",
  components: {
    ValidationProvider,
  },
  props: {
    zoneForm: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const chimeSongOptions = CHIME_SONG_OPTIONS;

    // Get field enablement logic
    const { zoneForm } = toRefs(props);
    const zoneType = computed(() => zoneForm.value.zoneType);
    const { advancedFields } = useZoneFieldsEnabled(zoneType);

    const areaSelectOptions = computed(() => {
      const options = [{ value: '0', text: '' }];
      const areas = store.getters["dmp_panels/getAreasList"] || [];
      if (!Array.isArray(areas)) return options;
      areas.forEach((area) => {
        options.push({
          value: String(area.id),
          text: `${String(area.id).padStart(2, "0")} - ${area.name}`,
        });
      });
      return options;
    });

    return {
      chimeSongOptions,
      advancedFields,
      areaSelectOptions,
    };
  },
};
</script>

<style scoped lang="scss">
.mb-75 {
  margin-bottom: 0.75rem;
}
</style>
